<template>
  <div v-hotkey="shortcuts" class="task-list-section">
    <div v-if="title" class="section-header">
      <BaseButton
        v-if="collapsible"
        kind="auxiliary"
        type="secondary"
        class="collapse-button"
        @click="toggleCollapse"
      >
        <template #title>
          <Avatar
            v-if="avatar"
            class="member-avatar-icon"
            :personish="avatar"
            size="small"
            badge-side="hide"
          />
          {{ title }}
          <span v-if="!loading" class="task-count">{{ count }}</span>
        </template>
        <template v-if="count > 0" #icon>
          <ChevronRightIcon :class="{ expanded: !isCollapsed }" />
        </template>
      </BaseButton>
      <span v-else class="title">
        <Avatar
          v-if="avatar"
          class="member-avatar-icon"
          :personish="avatar"
          size="small"
          badge-side="hide"
        />
        {{ title }}
        <span v-if="!loading" class="task-count">{{ count }}</span>
      </span>
    </div>
    <!-- An empty section ignores the (persisted) collapse state: it cannot
         be toggled anymore, and its content is at most the add-task input,
         which must stay reachable. While tasks are still resolving, keep
         collapsed sections collapsed instead of flashing them open. -->
    <div
      v-if="!isCollapsed || (count === 0 && !loading)"
      ref="taskListEl"
      class="section-tasks"
      :class="{ 'has-reorder-column': !!listKey }"
    >
      <AddTaskButton
        v-if="addTask"
        class="add-task-button"
        :add-task-action="{ type: 'task' }"
        :create-task="addTask"
        :post-validate-action="onTaskCreated"
        :metrics-source="'tasks/organization'"
        @focus="unselectTask"
      />
      <Loader v-if="loading" class="section-loading">
        <BaseSkeletonList is-compact :size="4" />
      </Loader>
      <TaskList
        v-else
        :actions="{
          assignee,
          open: true,
          postpone: true,
          hierarchy: true,
          schedule: {
            able: true,
            type: 'long',
          },
          notes: true,
          project: true,
          space: false,
        }"
        :task-ids="taskIds"
        :list-key="listKey"
        :selected-task-id="selectedTaskId"
        :is-editing="isEditing"
        :pagination="pagination"
        @quit-editing="quitEditing"
        @set-selected-task-el="(el) => setSelectedTaskEl(el)"
        @task-click="(id) => onTaskClick(id)"
      />
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, PropType, ref, toRefs } from "vue";

import type { GeneralizedTaskId } from "@ctrl/controller-rpc";

import AddTaskButton from "@/components/AddTaskButton.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import Avatar, { type Personish } from "@/components/elements/Avatar.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import { Shortcuts } from "@/components/shortcuts";
import { useTaskList } from "@/components/task/useTaskList";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useReleaser } from "@/ref";

import { applyTaskListOrder, getTaskListOrderStore } from "./ordering";
import TaskList from "./TaskList.vue";

/**
 * One retractable section of the org Tasks screen: a titled, collapsible
 * task list with optional inline task creation. Keyboard navigation stays
 * within the section; the mutual exclusion of selections across sibling
 * sections goes through the `unselectAllTasks` event.
 */
export default defineComponent({
  components: {
    AddTaskButton,
    Avatar,
    BaseButton,
    BaseSkeletonList,
    ChevronRightIcon,
    Loader,
    TaskList,
  },
  props: {
    /** When set, their avatar is shown before the title (Assigned tab). */
    avatar: { type: Object as PropType<Personish>, required: false },
    /** When set, show an add-task input on top of the list. */
    addTask: {
      type: Function as PropType<
        (title: string) => Promise<GeneralizedTaskId | undefined>
      >,
      required: false,
    },
    /**
     * Show the assignee chip on the tasks (default true). Disabled on the
     * Assigned tab, whose sections are already per-assignee.
     */
    assignee: { type: Boolean, default: true },
    collapsible: { type: Boolean, default: true },
    containerEl: { type: Object as PropType<HTMLElement> },
    /** Initial collapse state (persisted under `storageKey` afterwards). */
    defaultCollapsed: { type: Boolean, default: false },
    isAnySectionSelected: { type: Boolean, required: true },
    /**
     * Rows are still being mapped to task ids. Show a skeleton instead of
     * an empty list; hide the count so it does not flash 0.
     */
    loading: { type: Boolean, default: false },
    /**
     * Client-side page size. Unset on the Assigned tab: paging a stacked
     * per-member list leaves a load-more sentinel (and a large gap) above
     * the next member.
     */
    pagination: { type: Number, required: false },
    sectionIndex: { type: Number, required: true },
    sectionsNumber: { type: Number, required: true },
    /** Browser-store key persisting the collapse state. */
    storageKey: { type: String, required: false },
    /** Shared task-list ranking key (see `ordering.ts`). */
    listKey: { type: String, required: false, default: undefined },
    taskIds: {
      type: Array as PropType<GeneralizedTaskId[]>,
      required: true,
    },
    /** Section header; without one the list is plain (and never collapsed). */
    title: { type: String, required: false },
  },
  emits: {
    unselectAllTasks: () => true,
  },
  setup(props, { emit }) {
    const { containerEl, isAnySectionSelected, sectionIndex, sectionsNumber } =
      toRefs(props);
    const controller = inject.controller();
    const console = inject.consoleModal();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const contextMenu = inject.contextMenu();
    const profile = inject.profile();

    const taskIds = computed(() => props.taskIds);
    // Read-only ordered ids for keyboard selection. TaskList owns
    // useOrderableTaskList (seed + commit); duplicating the hook here only
    // added extra watchers.
    const orderStore = getTaskListOrderStore(profile.value.user.accountId);
    const selectableTaskIds = computed(() => {
      const key = props.listKey;
      if (!key) return taskIds.value;
      return applyTaskListOrder(
        taskIds.value,
        orderStore.value.lists[key] ?? []
      );
    });
    const count = computed(() => taskIds.value.length);

    const releaser = useReleaser();
    const { syncState } = useBrowserStore(profile.value.user.accountId);
    const isCollapsed =
      props.collapsible && props.title && props.storageKey
        ? releaser.add(
            syncState<boolean>(props.storageKey, props.defaultCollapsed)
          )
        : ref(false);

    const isSelectable = computed(() => ({
      withUp:
        sectionIndex.value === sectionsNumber.value - 1 &&
        !isAnySectionSelected.value,
      withDown: sectionIndex.value === 0 && !isAnySectionSelected.value,
    }));
    const {
      isEditing,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts: useTaskListShortcuts,
      // Must be bound in the template: up/down navigation no-ops while the
      // list element is unset (see selectUp/selectDown in useTaskList).
      taskListEl,
      unselectTask,
      release: releaseTaskList,
    } = useTaskList(
      selectableTaskIds,
      {
        controller,
        console,
        contextMenu,
        isModalShown,
        isInputFocused,
      },
      {
        isSelectable,
        containerEl,
      }
    );
    releaser.defer(releaseTaskList);

    const isSectionSelected = computed<boolean>(() =>
      selectedTaskId.value
        ? selectableTaskIds.value.includes(selectedTaskId.value)
        : false
    );

    const toggleCollapse = () => {
      // Empty sections have nothing to reveal: the header shows no arrow
      // and clicking it is a no-op. Loading is not empty yet.
      if (count.value === 0 && !props.loading) return;
      unselectTask();
      isCollapsed.value = !isCollapsed.value;
    };

    const onTaskClick = (taskId: string) => {
      emit("unselectAllTasks");
      selectTaskById(taskId);
    };
    // Select a task created through the add-task input right away (like the
    // agenda's TaskList): the selection shows as soon as the live list picks
    // up the new row.
    const onTaskCreated = (taskId: GeneralizedTaskId) => {
      emit("unselectAllTasks");
      selectTaskById(taskId);
    };
    // Up/down navigation stays within the section: at its first/last task
    // the keypress keeps the current selection (selectUp/selectDown no-op at
    // the bounds) instead of jumping to a sibling section.
    const shortcuts = computed<Shortcuts>(() =>
      isCollapsed.value ? {} : useTaskListShortcuts.value
    );

    return {
      count,
      isCollapsed,
      isEditing,
      isSectionSelected, // Exposed
      onTaskClick,
      onTaskCreated,
      quitEditing,
      selectedTaskId,
      setSelectedTaskEl,
      shortcuts,
      taskListEl,
      toggleCollapse,
      unselectTask, // Exposed
    };
  },
});
</script>
<style scoped lang="scss">
.task-list-section {
  display: flex;
  flex-direction: column;
  row-gap: 4px;

  .section-header {
    display: flex;
    align-items: center;
    // Align with TaskInline checkboxes (handle column 8+16+8 = 32px).
    margin-inline-start: 0;
    padding-inline-start: 32px;

    // Collapse control adds 4px pad — pull back so the label still matches.
    &:has(.collapse-button) {
      padding-inline-start: calc(32px - 4px);
    }
  }

  .title {
    @extend %font-h4;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-inline-end: 4px;
  }

  .task-count {
    font-weight: 300;
    @include themed() {
      color: t($grey-50);
    }
  }

  .member-avatar-icon {
    flex: 0 0 auto;
  }

  :deep(.collapse-button) {
    padding: 4px;

    .title {
      @extend .title;
    }

    .icon {
      svg {
        transition: transform 0.2s ease;

        &.expanded {
          transform: rotate(90deg);
        }
      }
    }
  }

  .section-tasks {
    display: flex;
    flex-direction: column;
    // AddTaskButton pad is 16px; with a reorder column, shift by the handle
    // gutters outside that pad (8+16+8 − 16 = 16px) so checkboxes line up.
    --task-row-pad: 16px;
    --task-reorder-column: 0px;

    &.has-reorder-column {
      --task-reorder-column: 16px;
    }

    .add-task-button {
      margin-inline-start: var(--task-reorder-column);
      width: calc(100% - var(--task-reorder-column));
    }

    // Class is on Loader's root (fallthrough) — :deep so scoped styles apply.
    // One padding value = Add task checkbox: reorder column + row pad.
    :deep(.section-loading) {
      box-sizing: border-box;
      padding-inline-end: var(--task-row-pad);
      padding-inline-start: calc(
        var(--task-reorder-column) + var(--task-row-pad)
      );

      // Match AddTaskButton lg checkbox (18px) and 12px gap, not compact 15/8.
      .base-skeleton-line {
        column-gap: 12px;

        .icon {
          min-width: 18px;
          max-width: 18px;
          height: 18px;
        }

        .body {
          height: 18px;
        }
      }
    }
  }
}
</style>
